import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Async thunks
export const fetchMeals = createAsyncThunk('admin/fetchMeals', async (_, { getState }) => {
    const { mealsStatus } = getState().admin;
    if (mealsStatus === 'succeeded') return; // Caching: Don't fetch if already successful

    const response = await axios.get("https://api.dailyfit.ae/api/admin/get-meals", { withCredentials: true });
    return response.data.data.meals;
});

export const fetchCategories = createAsyncThunk('admin/fetchCategories', async (_, { getState }) => {
    const { categoriesStatus } = getState().admin;
    if (categoriesStatus === 'succeeded') return;

    const response = await axios.get("https://api.dailyfit.ae/api/admin/get-categories", { withCredentials: true });
    return response.data.data;
});

export const fetchMealTypes = createAsyncThunk('admin/fetchMealTypes', async (_, { getState }) => {
    const { mealTypesStatus } = getState().admin;
    if (mealTypesStatus === 'succeeded') return;

    const response = await axios.get("https://api.dailyfit.ae/api/admin/get-mealtype", { withCredentials: true });
    return response.data.data;
});

const adminSlice = createSlice({
    name: 'admin',
    initialState: {
        meals: [],
        mealsStatus: 'idle', // idle | loading | succeeded | failed
        mealsError: null,

        categories: [],
        categoriesStatus: 'idle',
        categoriesError: null,

        mealTypes: [],
        mealTypesStatus: 'idle',
        mealTypesError: null,

        // Admin Info (Auth)
        adminInfo: localStorage.getItem('userInfo') ? JSON.parse(localStorage.getItem('userInfo')) : null,
    },
    reducers: {
        // Auth Reducer
        setAdmin: (state, action) => {
            state.adminInfo = action.payload;
            // Also update localStorage to keep in sync
            localStorage.setItem('userInfo', JSON.stringify(action.payload));
        },
        logoutAdmin: (state) => {
            state.adminInfo = null;
            localStorage.removeItem('userInfo');
        },

        // Standard reducer to force refresh if needed
        invalidateMeals: (state) => {
            state.mealsStatus = 'idle';
        },
        mealAdded: (state, action) => {
            state.meals.push(action.payload);
        },
        mealUpdated: (state, action) => {
            const index = state.meals.findIndex(meal => meal._id === action.payload._id);
            if (index !== -1) {
                state.meals[index] = action.payload;
            }
        },
        mealDeleted: (state, action) => {
            state.meals = state.meals.filter(meal => meal._id !== action.payload);
        }
    },
    extraReducers: (builder) => {
        builder
            // Meals
            .addCase(fetchMeals.pending, (state) => {
                if (state.mealsStatus === 'idle') {
                    state.mealsStatus = 'loading';
                }
            })
            .addCase(fetchMeals.fulfilled, (state, action) => {
                if (action.payload) { // Only update if we actually got data (not if we returned early)
                    state.mealsStatus = 'succeeded';
                    state.meals = action.payload;
                }
            })
            .addCase(fetchMeals.rejected, (state, action) => {
                state.mealsStatus = 'failed';
                state.mealsError = action.error.message;
            })
            // Categories
            .addCase(fetchCategories.pending, (state) => {
                if (state.categoriesStatus === 'idle') state.categoriesStatus = 'loading';
            })
            .addCase(fetchCategories.fulfilled, (state, action) => {
                if (action.payload) {
                    state.categoriesStatus = 'succeeded';
                    state.categories = action.payload;
                }
            })
            .addCase(fetchCategories.rejected, (state, action) => {
                state.categoriesStatus = 'failed';
                state.categoriesError = action.error.message;
            })
            // Meal Types
            .addCase(fetchMealTypes.pending, (state) => {
                if (state.mealTypesStatus === 'idle') state.mealTypesStatus = 'loading';
            })
            .addCase(fetchMealTypes.fulfilled, (state, action) => {
                if (action.payload) {
                    state.mealTypesStatus = 'succeeded';
                    state.mealTypes = action.payload;
                }
            })
            .addCase(fetchMealTypes.rejected, (state, action) => {
                state.mealTypesStatus = 'failed';
                state.mealTypesError = action.error.message;
            });
    },
});

export const { invalidateMeals, mealAdded, mealUpdated, mealDeleted, setAdmin, logoutAdmin } = adminSlice.actions;

export const selectAllMeals = (state) => state.admin.meals;
export const selectMealsStatus = (state) => state.admin.mealsStatus;
export const selectAllCategories = (state) => state.admin.categories;
export const selectAllMealTypes = (state) => state.admin.mealTypes;
export const selectAdmin = (state) => state.admin.adminInfo;

export default adminSlice.reducer;
